import { useEffect, useState } from "react";
import { api, apiError } from "./api";
import { Card, PageHead, Pill, Btn, Input, Field, Confirm } from "./widgets";
import { toast } from "sonner";
import { useAdmin } from "./AdminApp";

const COMPANY_FIELDS = [
  ["companyName", "Company name"],
  ["founderName", "Founder name"],
  ["phone", "Phone"],
  ["email", "Email (hidden publicly while empty)"],
  ["addressStreet", "Address — street"],
  ["addressSuite", "Address — suite"],
  ["addressCity", "Address — city"],
  ["addressState", "Address — state"],
  ["addressZip", "Address — ZIP"],
  ["hoursDaysEn", "Office days (EN)"],
  ["hoursDaysEs", "Office days (ES)"],
  ["hoursOpen", "Opens"],
  ["hoursClose", "Closes"],
  ["instagram", "Instagram URL"],
  ["facebook", "Facebook URL"],
  ["tiktok", "TikTok URL"],
  ["linkedin", "LinkedIn URL"],
];

const Settings = () => {
  const { user } = useAdmin();
  const isSuper = user.role === "super_admin";
  const [s, setS] = useState(null);
  const [company, setCompany] = useState({});
  const [newPw, setNewPw] = useState("");
  const [confirmMode, setConfirmMode] = useState(null);
  const [confirmPw, setConfirmPw] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  const load = () =>
    api.get("/admin/settings").then((r) => {
      setS(r.data);
      setCompany(r.data.company || {});
    }).catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
  }, []);

  const changeMode = async (mode) => {
    try {
      await api.patch("/admin/settings", { accessMode: mode });
      toast.success("Website status changed.");
      setConfirmMode(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
      setConfirmMode(null);
    }
  };

  const changePassword = async () => {
    try {
      await api.post("/admin/settings/preview-password", { password: newPw });
      toast.success("Preview password changed. All preview sessions were logged out.");
      setNewPw("");
      setConfirmPw(false);
    } catch (e) {
      toast.error(apiError(e));
      setConfirmPw(false);
    }
  };

  const saveCompany = async () => {
    try {
      await api.patch("/admin/settings", { company });
      toast.success("Company settings saved — live on the website.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!s) return <p className="text-sm text-[#171717]/50">Loading…</p>;

  const MODES = [
    { key: "public", label: "Public" },
    { key: "private", label: "Private Preview" },
    { key: "maintenance", label: "Maintenance" },
  ];

  return (
    <div data-testid="settings-page">
      <PageHead title="Settings" sub="Website access, company information and integrations." />

      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="border-l-2 border-l-[#C6A46A]" data-testid="website-access">
          <p className="mb-4 text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">Website access</p>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-[#171717]/60">Current status:</span>
            <Pill tone={s.accessMode === "public" ? "green" : s.accessMode === "private" ? "champagne" : "red"}>
              {s.accessMode}
            </Pill>
          </div>
          {isSuper ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {MODES.filter((m) => m.key !== s.accessMode).map((m) => (
                <Btn key={m.key} onClick={() => setConfirmMode(m.key)} data-testid={`settings-mode-${m.key}`}>
                  Switch to {m.label}
                </Btn>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-xs text-[#171717]/45">Only the Super Admin can change the website status.</p>
          )}

          <div className="mt-8 border-t border-[#171717]/10 pt-6">
            <p className="text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">Preview password</p>
            <div className="mt-3 flex items-center gap-3">
              <span className="font-mono text-sm tracking-[0.3em] text-[#171717]/70">{s.previewPassword || "not set"}</span>
              <span className="text-xs text-[#171717]/40">· {s.activePreviewSessions} active preview session(s)</span>
            </div>
            {isSuper && (
              <div className="mt-4 flex flex-wrap gap-2">
                <Input
                  type="password"
                  placeholder="New password (min 10 chars)"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  className="max-w-64"
                  data-testid="preview-password-new"
                />
                <Btn primary onClick={() => newPw.length >= 10 && setConfirmPw(true)} data-testid="preview-password-change">
                  Change Preview Password
                </Btn>
                <Btn onClick={() => setConfirmLogout(true)} data-testid="preview-logout-all">Logout All Preview Sessions</Btn>
              </div>
            )}
          </div>
        </Card>

        <Card data-testid="integrations">
          <p className="mb-4 text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">Integrations</p>
          <div className="flex items-center justify-between border border-[#171717]/10 p-4">
            <div>
              <p className="text-sm text-[#171717]">Email — Resend</p>
              <p className="mt-1 text-xs text-[#171717]/50">
                CRM records are the primary store. Email is only a notification layer — a failed email never loses a lead.
              </p>
            </div>
            <Pill tone={s.resend?.connected ? "green" : "gray"}>{s.resend?.connected ? "Connected" : "Not connected"}</Pill>
          </div>
          <p className="mt-3 text-[0.65rem] text-[#171717]/40">
            Connection is configured at the infrastructure level (API key + notification recipient). Contact the Super Admin to activate.
          </p>

          <div className="mt-6 border-t border-[#171717]/10 pt-6">
            <p className="mb-3 text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">Brand</p>
            <div className="flex gap-4">
              {[["Carbon", "#171717"], ["Champagne", "#C6A46A"], ["Ivory", "#F4F0E8"]].map(([name, hex]) => (
                <div key={name} className="flex items-center gap-2">
                  <span className="h-6 w-6 rounded-[2px] border border-[#171717]/15" style={{ backgroundColor: hex }} />
                  <span className="text-xs text-[#171717]/60">{name} {hex}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[0.65rem] text-[#171717]/40">
              Official brand colors are locked to the identity guide. Logo and favicon files are managed as brand assets.
            </p>
          </div>
        </Card>

        <Card className="xl:col-span-2" data-testid="company-settings">
          <div className="mb-5 flex items-center justify-between">
            <p className="text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">Company information</p>
            <Btn primary onClick={saveCompany} data-testid="company-save">Save Company Settings</Btn>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {COMPANY_FIELDS.map(([key, label]) => (
              <Field key={key} label={label}>
                <Input
                  value={company[key] || ""}
                  onChange={(e) => setCompany({ ...company, [key]: e.target.value })}
                  data-testid={`company-${key}`}
                />
              </Field>
            ))}
          </div>
          <p className="mt-4 text-[0.65rem] text-[#171717]/40">
            Empty fields stay hidden on the public website. Saving updates the live site immediately.
          </p>
        </Card>
      </div>

      <Confirm
        open={Boolean(confirmMode)}
        title={`Switch website to ${confirmMode}?`}
        body={confirmMode === "public"
          ? "Are you sure you want to make AviahFinancial.com public? Anyone will be able to access it."
          : "Are you sure you want to place AviahFinancial.com in Private Preview / Maintenance mode? Public visitors will see the construction page."}
        confirmLabel="Yes, switch"
        onConfirm={() => changeMode(confirmMode)}
        onCancel={() => setConfirmMode(null)}
      />
      <Confirm
        open={confirmPw}
        title="Change the preview password?"
        body="All active preview sessions will be logged out and must sign in again with the new password."
        confirmLabel="Change password"
        onConfirm={changePassword}
        onCancel={() => setConfirmPw(false)}
      />
      <Confirm
        open={confirmLogout}
        title="Log out all preview sessions?"
        body="Every visitor currently browsing with preview access will be returned to the construction page."
        confirmLabel="Log out all"
        onConfirm={async () => {
          try {
            await api.post("/admin/settings/preview-logout-all");
            toast.success("All preview sessions cleared.");
            setConfirmLogout(false);
            load();
          } catch (e) {
            toast.error(apiError(e));
          }
        }}
        onCancel={() => setConfirmLogout(false)}
      />
    </div>
  );
};

export default Settings;
